import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE, KDS_CACHE_KEY } from '../ticketConstants';
import { useOrderSignal, SIGNAL_COALESCE_MS } from './useOrderSignal';
import { localDateISO } from '../lib/dateUtils';
import { reconcileOrders } from '../lib/reconcileOrders';

/**
 * Polls /api/square-tickets for live Square orders.
 * PRIMARY  → refetches the moment the webhook signals a change (useOrderSignal:
 *            a Firestore push, falling back to polling /api/order-signal).
 * FALLBACK → full doFetch() every pollIntervalMs regardless.
 *
 * `relaxedPollMs` (optional): once this board has actually received a webhook
 * signal, the push is proven to work end to end, so the backup poll slows to
 * this interval. Until then — or if webhooks never arrive — it stays at
 * pollIntervalMs, so a broken webhook never makes new orders slower to appear.
 *
 * Both timers stop while the tab is hidden and resume with an immediate fetch,
 * so a board nobody is looking at costs nothing — see `visible` below.
 */
export function useLiveOrders(pollIntervalMs = 10000, { relaxedPollMs = 0 } = {}) {
  // Last fetch from this device, shown until the first live answer — today's
  // orders only, so a board opened in the morning never flashes yesterday's.
  const [orders, setOrders] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(KDS_CACHE_KEY) || '[]');
      const today = localDateISO();
      return Array.isArray(cached)
        ? cached.filter(o => o && o.createdAt && localDateISO(new Date(o.createdAt)) === today)
        : [];
    } catch { return []; }
  });
  // What `orders` holds right now, so a fetch can tell whether anything changed.
  const ordersRef = useRef(orders);
  // Server time (`fetchedAt`) of the data on screen — see the overlap guard below.
  const appliedAtRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [updateSource, setUpdateSource] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // ── Pause while hidden ──────────────────────────────────────────────────────
  // A kitchen iPad left on overnight kept both timers running against a board
  // nobody could see — ~17k Firestore reads a day (via /api/order-signal) for a
  // hidden tab. Gating on visibility costs those hours nothing, and because the
  // effects below re-run on the way back, becoming visible triggers an immediate
  // fetch — the board is never stale by the time it's on screen.
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden');

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  // `afterMs` (webhook refetches): the time of the order change, so the server
  // won't answer from a cache taken before it.
  const doFetch = useCallback(async (src = 'poll', afterMs = null) => {
    try {
      const qs = Number.isFinite(afterMs) && afterMs > 0 ? `?after=${Math.floor(afterMs)}` : '';
      const res = await window.fetch(`${API_BASE}/api/square-tickets${qs}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      if (!mountedRef.current) return;
      // A webhook refetch and a poll can overlap. If the older answer (its
      // server snapshot time, `fetchedAt`) arrives last, drop it rather than roll
      // the board back until the next fetch — up to a relaxed 30 s poll.
      const dataAt = Date.parse(json.fetchedAt);
      if (Number.isFinite(dataAt)) {
        if (dataAt < appliedAtRef.current) { setError(null); return; }
        appliedAtRef.current = dataAt;
      }
      const incoming = (json.orders || []).slice().sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      );
      // Unchanged orders keep their objects; a poll with no changes at all keeps
      // the whole list, so nothing downstream re-renders and nothing is rewritten.
      const next = reconcileOrders(ordersRef.current, incoming);
      if (next !== ordersRef.current) {
        ordersRef.current = next;
        setOrders(next);
        try { localStorage.setItem(KDS_CACHE_KEY, JSON.stringify(next)); } catch {}
      }
      setLastFetch(new Date());
      setUpdateSource(src);
      setError(null);
    } catch (e) {
      if (mountedRef.current) setError(e.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Webhook-driven instant updates. Same gating the old 5 s signal poll had:
  // only while polling is on and the board is on screen.
  const onSignal = useCallback((changedAtMs) => doFetch('webhook', changedAtMs), [doFetch]);
  // A burst of events for one order change → one refetch (SIGNAL_COALESCE_MS).
  const { proven } = useOrderSignal(pollIntervalMs !== 0 && visible, onSignal, { coalesceMs: SIGNAL_COALESCE_MS });
  const pollMs = pollIntervalMs > 0 && proven && relaxedPollMs > pollIntervalMs
    ? relaxedPollMs : pollIntervalMs;

  // Fetch once whenever the tab is (or becomes) visible — the catch-up fetch on
  // resume — and when the requested interval changes. Not when the backup poll
  // merely relaxes: that moment is a webhook refetch already.
  useEffect(() => {
    if (visible) doFetch('poll');
  }, [doFetch, pollIntervalMs, visible]);

  // Backup poll at the effective interval (0 = paused).
  useEffect(() => {
    if (!visible || pollMs === 0) return undefined;
    const id = setInterval(() => doFetch('poll'), pollMs);
    return () => clearInterval(id);
  }, [doFetch, pollMs, visible]);

  // Stable, so the board's wake/focus listeners (keyed on it) aren't re-attached
  // on every render.
  const refetch = useCallback(() => doFetch('poll'), [doFetch]);

  return { orders, loading, error, lastFetch, refetch, updateSource, pollMs };
}
